import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentReference } from '@angular/fire/compat/firestore';
import {
  client_rates_default,
  client_services_default,
  client_vendors_default,
} from 'src/app/models/clients/client-default-values';
import { Patient } from 'src/app/models/patient.model';
import { Lead } from 'src/app/models/user.model';
import { environment } from 'src/environments/environment';

export interface CreateUserDto {
  practice: string;
  fName: string;
  lName: string;
  uid?: string;
  clientId?: string;
  practiceAdmin?: boolean;
  provider?: boolean;
  patient?: boolean;
  pwHash?: string;
  npi?: string;
  phoneNumber?: string;
  isWelbyClinical?: boolean;
  lead?: Lead;
  highestLicensure?: string;
  /** signup_requests id from the invitation link; the provider sign-up endpoint requires it. */
  invitationId?: string;
}

@Injectable({ providedIn: 'root' })
export class UserFactory {
  constructor(private db: AngularFirestore) {}

  async createNewClient(emailAddress: string, data: CreateUserDto) {
    // create a new client object
    const newClient = { client_name: data.practice, client_npi: data.npi };
    // get a reference to the clients collection
    const clientsRef: DocumentReference = await this.db.collection('clients').add(newClient);
    // get the client id
    const clientId = clientsRef.id;

    try {
      await clientsRef.set(
        {
          active: true,
          billing_approved: false,
          ccm_clinical: { active: true, rate: 30 },
          client_id: clientId,
          client_name: data.practice,
          client_npi: data.npi,
          device_vendors: client_vendors_default,
          emr_data: {},
          rates: client_rates_default,
          rpm_clinical: { active: true, rate: 30 },
          software_license: { active: true, rate: 40 },
          start_date: new Date(),
          patients_loaded: 0,
          service: client_services_default,
        },
        { merge: true }
      );
    } catch (e) {
      throw new Error('Error trying to set client data');
    }

    try {
      const details = {
        active: true,
        name: `${data.fName} ${data.lName}`,
        primary_email: emailAddress,
        primary_phone: '',
        roles: {
          admin: true,
          billing: true,
          clinical: true,
        },
        uid: clientId,
      };
      await clientsRef.collection('client_contacts').doc(clientId).set(details);
    } catch (e) {
      throw new Error('Error trying to set client contacts data');
    }
    return clientId;
  }

  async createUserAccount(emailAddress: string, data: CreateUserDto): Promise<Patient> {
    try {
      const activeUser = {
        client_access: [data.clientId],
        client_accounts: [data.clientId],
        client_responsible: data.practice,
        client_responsible_id: data.clientId,
        ...(data.isWelbyClinical && { chatEnabled: true }),
        // Gated on `data.provider` (not just `isWelbyClinical`) so the flag is written deterministically
        // for every provider account — clinical onboards get `true`, other providers an explicit `false` —
        // while patients omit it entirely. Previously it keyed only on `isWelbyClinical`, so non-clinical
        // providers were left with the field absent rather than a defined value.
        ...(data.provider && { enable_conference_call: !!data.isWelbyClinical }),
        dob: new Date(),
        email: emailAddress,
        pw_hash: data.pwHash,
        firstName: data.fName,
        lastName: data.lName,
        phone: data.phoneNumber ? data.phoneNumber : 'N/A',
        profileImageURL: `${environment?.defaultPhotoUrl ?? ''}`,
        ssn_id: 'n/a',
        timeStamp: new Date(),
        user_id: data.uid,
        roles: {
          isPatient: data.patient,
          isClient: data.provider,
          isAdmin: false,
          isPracticeAdmin: data.practiceAdmin,
          isWelbyClinical: data.isWelbyClinical,
          isManagerNurse: false,
        },
        ...(data.isWelbyClinical && { lead: data.lead }),
        ...(data.isWelbyClinical && { highest_licensure: data.highestLicensure }),
      };

      await this.db.collection('users').doc(data.uid).set(activeUser);
      return activeUser;
    } catch (error) {
      // Rethrown, not swallowed: returning null let the sign-up carry on with no users document, leaving
      // a Firebase account that could neither sign in nor register again (email-already-in-use).
      console.error('Could not create the users document', error);
      throw error;
    }
  }
}
